import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BackendPagination } from "../models/pagination.model";
import {
	DataTableSortOrderType,
	ITablePagination,
} from "../models/components/table/table-pagination.model";
import { IReminder } from "../models/entities/reminder.model";
import { EndpointsService } from "../services/endpoints/endpoints.service";
import { buildMyRemindersFilters } from "../pages/home/worklist/reminders/reminders.service";

/**
 * The logged-in user's unresolved reminders for the Home worklist (KAN-2555).
 *
 * 🔒 THIS HOOK IS THE ONE PLACE THE SCOPE IS DECIDED. Nothing else in the
 * Reminders section knows which URL the rows came from or which filters were
 * sent — the section, the row, the grouping and the tests are identical under
 * either scope.
 *
 * Today it calls `GET /api/reminders/` with `assignedTo == me`. For a COACH the
 * backend silently ANDs its own `coach_id == me` panel filter
 * (`AuthorizationChecker.validateFiltersOwnership`), so what actually renders is
 * "assigned to me AND on my panel". Maria asked for assignment ALONE
 * (KAN-2555, 2026-09-16); that needs a `gladstone-data-retriever` change —
 * a new `GET /api/reminders/assigned-to-me` plus the matching write/navigation
 * carve-out — which widens PHI access and is therefore gated on Nandan's
 * sign-off rather than a product green-light.
 *
 * WHEN THAT LANDS, THE FLIP IS THIS FILE AND NOTHING ELSE: point
 * `queryFn` at the new endpoint (and drop the now-redundant `assignedTo`
 * filter from `buildMyRemindersFilters`). Deliberately isolated here so that
 * change stays a few lines.
 */

export const MY_REMINDERS_QUERY_KEYS = ["participants-reminders", "participants-reminders-count"];

/**
 * Maria asked for the whole list rather than a top-N with a "see all", so this
 * requests one large page. The 30-day floor already bounds what can come back;
 * this bounds the pathological case as well, since the query runs on every
 * coach's login.
 */
export const MY_REMINDERS_PAGE_SIZE = 200;

export const MY_REMINDERS_DEFAULT_PAGINATION: ITablePagination = {
	first: 0,
	rows: MY_REMINDERS_PAGE_SIZE,
	page: 0,
	// Oldest due date first — the same server-side ordering the global
	// Reminders page uses. The section re-sorts each group in the pure core
	// (`groupReminders`), because within a single day the server has no
	// tiebreak at all and can return the rows in a different order per load.
	sortField: "dateOn",
	sortOrder: 1 as DataTableSortOrderType,
	search: undefined,
};

export const buildMyRemindersPagination = (assignedToId: number, today: Date): BackendPagination =>
	new BackendPagination({
		...MY_REMINDERS_DEFAULT_PAGINATION,
		filters: buildMyRemindersFilters(assignedToId, today),
	});

export interface UseMyRemindersResult {
	reminders: IReminder[];
	isLoading: boolean;
	isError: boolean;
	error: unknown;
	/** Refetch this list after a resolve or an edit. */
	invalidate: () => void;
}

export const useMyReminders = (
	assignedToId: number | undefined | null,
	today: Date
): UseMyRemindersResult => {
	const queryClient = useQueryClient();

	const backendPagination = useMemo(
		() => buildMyRemindersPagination(assignedToId ?? -1, today),
		[assignedToId, today]
	);

	/**
	 * Refresh on every page entry, never on a timer — the same freshness policy
	 * as the critical-alerts section. Home is a route, so mounting IS entering
	 * the page.
	 */
	const query = useQuery({
		queryKey: ["participants-reminders", backendPagination],
		queryFn: async () =>
			await EndpointsService.dataRetriever.getReminders({
				config: { params: backendPagination },
			}),
		enabled: !!assignedToId,
		staleTime: 0,
		refetchOnMount: "always" as const,
		refetchOnWindowFocus: false,
	});

	return {
		// NEVER collapse an error into an empty list — a failed load must not
		// read as "nothing to do today" (plan §11.6). The section frame also
		// hides the count badge entirely when isError is true.
		reminders: query.data ?? [],
		isLoading: query.isLoading,
		isError: !!query.error,
		error: query.error,
		invalidate: () =>
			MY_REMINDERS_QUERY_KEYS.forEach((key) =>
				queryClient.invalidateQueries({ queryKey: [key] })
			),
	};
};
